import APIService from "./APIService";

/**
 * API Service for MFA (Multi-Factor Authentication) operations
 */
const MfaAPI = {
  /**
   * Get MFA status for current user
   * @returns {Promise} Promise that resolves with MFA status
   */
  getMfaStatus: async () => {
    return APIService.get("/api/v1/base/mfa/status");
  },

  /**
   * Initiate MFA setup (enable MFA)
   * Uses the user's existing verified phone number
   * @param {string} method - MFA method ("sms")
   * @returns {Promise} Promise that resolves with setup session info
   */
  enableMfa: async (method) => {
    return APIService.post("/api/v1/base/mfa/enable", {
      method,
    });
  },

  /**
   * Complete MFA setup by verifying code
   * @param {string} setupSessionId - Setup session ID
   * @param {string} code - Verification code
   * @returns {Promise} Promise that resolves with backup codes
   */
  verifyMfaSetup: async (setupSessionId, code) => {
    return APIService.post("/api/v1/base/mfa/enable/verify", {
      setupSessionId,
      code,
    });
  },

  /**
   * Disable MFA for current user
   * @param {string} password - User's password for confirmation
   * @returns {Promise} Promise that resolves with success message
   */
  disableMfa: async (password) => {
    return APIService.post("/api/v1/base/mfa/disable", {
      password,
    });
  },

  /**
   * Regenerate backup codes
   * @param {string} password - User's password for confirmation
   * @returns {Promise} Promise that resolves with new backup codes
   */
  regenerateBackupCodes: async (password) => {
    return APIService.post("/api/v1/base/mfa/regenerate-backup-codes", {
      password,
    });
  },

  /**
   * Verify MFA code during login
   * @param {string} mfaSessionId - MFA session ID from login response
   * @param {string} code - Verification code (optional if using backup code)
   * @param {string} backupCode - Backup code (optional if using regular code)
   * @returns {Promise} Promise that resolves with auth response
   */
  verifyMfaLogin: async (mfaSessionId, code, backupCode) => {
    return APIService.post("/api/v1/public/auth/login/verify-mfa", {
      mfaSessionId,
      code,
      backupCode,
    });
  },

  /**
   * Resend MFA verification code
   * @param {string} mfaSessionId - MFA session ID
   * @returns {Promise} Promise that resolves with success message
   */
  resendMfaCode: async (mfaSessionId) => {
    return APIService.post("/api/v1/public/auth/login/resend-mfa-code", {
      mfaSessionId,
    });
  },
};

export default MfaAPI;
